import {
  JsonSchema,
  JsonSchemaProperty,
  TransformedProperty,
  UIOptionsTypes as UIOptionsType,
} from "../types/utils/createUI";
import RowFieldTemplate from "../components/ScenarioForm/FieldTemplates/RowFieldTemplate";
import TitleFieldTemplate from "../components/ScenarioForm/FieldTemplates/TitleFieldTemplate";
import CollapsibleFieldTemplate from "../components/ScenarioForm/FieldTemplates/CollapsibleFieldTemplate";
import GridFieldTemplate from "../components/ScenarioForm/FieldTemplates/GridFieldTemplate";
import HorizontalFieldTemplate from "../components/ScenarioForm/FieldTemplates/HorizontalFieldTemplate";
import SelectWithInputWidget from "../components/ScenarioForm/Widgets/SelectWithInputWidget";
import CalculateFieldWidget from "../components/ScenarioForm/Widgets/CalculateFieldWidget";
import StyledArrayFieldTemplate from "../components/ScenarioForm/FieldTemplates/StyledArrayFieldTemplate";
import CollapsibleArrayFieldTemplate from "../components/ScenarioForm/FieldTemplates/CollapsibleArrayFieldTemplate";
import RadioWidget from "../components/ScenarioForm/Widgets/RadioWidget";
import ToggleWidget from "../components/ScenarioForm/Widgets/ToggleWidget";
import InputWithUnitWidget from "../components/ScenarioForm/Widgets/InputWithUnitWidget";
import CheckboxIconWidget from "../components/ScenarioForm/Widgets/CheckboxIconWidget";
import MaatregelenObjectTemplate from "../components/ScenarioForm/FieldTemplates/MaatregelenObjectTemplate";
import MaatregelenRowTemplate from "../components/ScenarioForm/FieldTemplates/MaatregelenRowTemplate";
import MeasureAnswersObjectTemplate from "../components/ScenarioForm/FieldTemplates/MeasureAnswersObjectTemplate";
import HnnNiveauWidget from "../components/ScenarioForm/Widgets/HnnNiveauWidget";
import SelectWithSearch from "../components/ScenarioForm/Widgets/SelectWithSearchWidget/SelectWithSearch";
import TelephoneWidget from "../components/ScenarioForm/Widgets/TelephoneWidget";
import CheckboxesWidget from "../components/ScenarioForm/Widgets/CheckboxesWidget";
//import ArrayField from "@rjsf/core/lib/components/fields/ArrayField.js";

export function createUI(inputJson: JsonSchema): UIOptionsType {

  const outputJson: UIOptionsType = {
    "ui:submitButtonOptions": {
      props: {
        className: "button button-secondary",
      },
    },
    "ui:title": false,
    "ui:description": false,
  };

  // Extract projectType from meta data to pass to HnnNiveauWidget
  const projectType = inputJson?.projectType;
  const isPrestatiesPage = inputJson.title?.trim().toLowerCase().includes("prestaties");

  // Special handling for Maatregelen object with categories at root level
  if (inputJson.type === "object" && inputJson.meta?.categories) {
    outputJson["ui:ObjectFieldTemplate"] = MaatregelenObjectTemplate;
    outputJson["ui:options"] = {
      orderable: false,
      label: false,
    };
  }

  function transformProperties(
    properties: Record<string, JsonSchemaProperty>,
    depth = 0,
    tableView = false,
    parentKey?: string
  ): Record<string, TransformedProperty> {
    const transformed: Record<string, TransformedProperty> = {};
    let detailheader = false;
    let sortedKeys: string[] = [];
    let isTable = tableView;

    for (const key in properties) {
      if (properties.hasOwnProperty(key)) {
        const property = properties[key];
        const transformedProperty: TransformedProperty = {};

        // Sort properties based on the 'order' key
        if (property.properties) {
          sortedKeys = Object.keys(property.properties).sort((a, b) => {
            const orderA = property.properties![a].order ?? 0;
            const orderB = property.properties![b].order ?? 0;
            if (
              a.endsWith("_question") &&
              b === a.replace("_question", "_input")
            ) {
              return -1;
            }
            if (
              b.endsWith("_question") &&
              a === b.replace("_question", "_input")
            ) {
              return 1;
            }
            return orderA - orderB;
          });
        }

        //The first level of properties should be collapsible
        if (depth === 0) {
          if (property.table === true) {
            isTable = true;
            // For table properties at first level, use HorizontalFieldTemplate to put title horizontal to the children
            transformedProperty["ui:ObjectFieldTemplate"] = HorizontalFieldTemplate;
          } else if (property.type === "array" || (Array.isArray(property.type) && property.type.includes("array"))) {
            // For array fields at the first level, use CollapsibleArrayFieldTemplate
            transformedProperty["ui:ArrayFieldTemplate"] = CollapsibleArrayFieldTemplate;

            // Special handling for arrays with displayAs: "table" (like measure categories)
            if (property.displayAs === "table") {
              // Use CollapsibleFieldTemplate for the array container instead
              delete transformedProperty["ui:ArrayFieldTemplate"];
              transformedProperty["ui:ArrayFieldTemplate"] = CollapsibleArrayFieldTemplate;
            }
          } else {
            // For other types at first level, use CollapsibleFieldTemplate
            transformedProperty["ui:ObjectFieldTemplate"] = CollapsibleFieldTemplate;
          }
        } else if (depth === 1) {
          if (property.table === true) {
            transformedProperty["ui:ObjectFieldTemplate"] = GridFieldTemplate;
          }
          if (property.properties) {
            detailheader = true;
          }
        }

        //if iconPath then use custom CheckboxWithIcon widget
        if (property.iconPath) {
          transformedProperty["ui:widget"] = CheckboxIconWidget;
          transformedProperty["ui:options"] = {
            label: false,
          };
        }

        if (property.icon && property.unit) {
          transformedProperty["ui:widget"] = InputWithUnitWidget;
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            unit: property.unit,
            label: false,
            hideLabel: true,
          };

        }

        //select with input option for maatregelen options
        if (key.includes("maatregel") && property.options) {
          transformedProperty["ui:widget"] = SelectWithInputWidget;
          transformedProperty["ui:options"] = {
            label: false,
          };
        }
        if (key.includes("toelichting") || key.includes("belemmering")) {

          transformedProperty["ui:options"] = {
            label: false,
          }
        }


        //pair questions are always displayed in a row (next to eachother)
        if (key.includes("pair")) {
          transformedProperty["ui:ObjectFieldTemplate"] = RowFieldTemplate;
          transformedProperty["ui:title"] = false;
        }

        //array in styled custom field template
        if (
          property.type === "array" &&
          property.items &&

          depth > 0 // Only apply this to nested arrays, not first level
        ) {
          transformedProperty["ui:ArrayFieldTemplate"] = StyledArrayFieldTemplate;
          transformedProperty["ui:options"] = {
            orderable: false,
            label: false,
          };
        }

        //boolean properties should be displayed as radio buttons with Dutch labels
        //or as toggle switches in table view
        //unless they already have an iconPath (which uses CheckboxIconWidget)
        //or unless they have an enum [true] value -> which indicated the answer of the question should be true and be shown as the default checkbox of the form
        if (property?.format == 'tel') {
          transformedProperty["ui:widget"] = TelephoneWidget;
          
        }
        if (
          ((Array.isArray(property.type) && property.type[0] === "boolean") ||
            property.type === "boolean") &&
          !property.iconPath
        ) {
          if (isTable) {
            transformedProperty["ui:widget"] = ToggleWidget; // Use toggle widget for table view
          } else if (!property.enum?.[0] === true) {
            transformedProperty["ui:widget"] = RadioWidget;
          }
        }

        //HNN-niveau fields should use the HnnNiveauWidget to dynamically set default values
        if (property.title === "HNN-niveau") {
          transformedProperty["ui:widget"] = HnnNiveauWidget;
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            projectType: projectType,
          };
        }
        // render a select with search when the enum contains at least one string
        if ('enum' in property && Array.isArray(property.enum) && property.enum.length > 0 && property.enum.some((item: unknown) => typeof item === 'string')) {

          //on Maatregelen page it shoudl be SelectWithInputWidget
          if (key.includes("maatregel")) {
            transformedProperty["ui:widget"] = SelectWithInputWidget;
            transformedProperty["ui:options"] = {
              label: false,
            };
          } else {
            transformedProperty["ui:widget"] = SelectWithSearch;
          }
        }


        //Add custom title field template to properties with a title
        if (property.title) {
          let schemaForTitle = property;

          if (isTable && (property.unit || property.readOnly === true) && !property.iconPath && property.title !== "HNN-niveau") {
            // In table view, use InputWithUnitWidget for fields with units or readonly fields
            // unless they have iconPath (which should use CheckboxIconWidget)
            transformedProperty["ui:title"] = (
              <TitleFieldTemplate
                title={property.title}
                schema={''}
                id={key}
                depth={depth}
                detailheader={detailheader}
              />
            );
            transformedProperty["ui:widget"] = InputWithUnitWidget;
            transformedProperty["ui:options"] = {
              ...transformedProperty["ui:options"],
              unit: property.unit,
              projectType: projectType,
            };

            // Create a copy of the schema without the unit for the title
            schemaForTitle = { ...property };
            delete schemaForTitle.unit;
          } else if (isTable && property.unit && property.iconPath && property.icon || (property.title && property.title.toLowerCase() === "belemmeringen")) {
            // For items with both unit and iconPath in table view, hide the title completely
            transformedProperty["ui:title"] = false;
            transformedProperty["ui:options"] = {
              ...transformedProperty["ui:options"],
              label: false,
              hideLabel: true,
            };

          }
          else {
            transformedProperty["ui:title"] = (
              <TitleFieldTemplate
                title={property.title}
                schema={schemaForTitle}
                id={key}
                depth={depth}
                detailheader={detailheader}
              />
            );
          }

        }

        // The measure answers title is already shown in the table column header.
        if (key === "measure_answers") {
          transformedProperty["ui:ObjectFieldTemplate"] = MeasureAnswersObjectTemplate;
          transformedProperty["ui:title"] = false;
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            label: false,
            hideLabel: true,
          };
        }

        if (parentKey === "measure_answers") {
          transformedProperty["ui:title"] = false;
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            label: false,
            hideLabel: true,
          };
        }

        if (Array.isArray(property.type) && property.type && property.type[0] === "number") {
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            inputType: "number"
          };
        }

        // Handle textarea widget
        if (property?.widget == 'textarea') {
          transformedProperty["ui:widget"] = "textarea";
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            rows: isPrestatiesPage ? 2 : 4,
          };
        }
        // Handle multi-select checkboxes widget
        if (property?.widget == 'checkboxes') {
          transformedProperty["ui:widget"] = CheckboxesWidget;
        }
        // Handle computed calculation widget
        if (property.computedCalculation) {
          transformedProperty["ui:widget"] = CalculateFieldWidget;
        }
        //loop through nested properties
        if (property.properties) {
          const nestedTransformed = transformProperties(
            property.properties,
            depth + 1,
            isTable,
            key
          );
          if (Object.keys(nestedTransformed).length > 0) {
            Object.assign(transformedProperty, nestedTransformed);
          }
        }

        // Handle array items with properties (for nested schemas like measure categories)
        if (property.type === "array" && property.items &&
          typeof property.items === "object" && !Array.isArray(property.items) &&
          property.items.type === "object" && property.items.properties) {


          // Transform the properties within array items
          const itemsTransformed = transformProperties(
            property.items.properties,
            depth + 1,
            false
          );

          if (Object.keys(itemsTransformed).length > 0) {
            // Create the items object in the UI schema
            transformedProperty["items"] = {
              // For measure categories with displayAs: "table", use MaatregelenRowTemplate for individual items
              ...(property.displayAs === "table" ? { "ui:ObjectFieldTemplate": MaatregelenRowTemplate } : {}),
              ...itemsTransformed
            };
          }

          // Set options for array fields
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            orderable: false,
            label: false,
          };
        }

        // Handle simple array items (like string arrays) - specifically for belemmeringen
        if (property.type === "array" && property.items &&
          typeof property.items === "object" && !Array.isArray(property.items) &&
          property.items.type === "string" &&
          property.title && property.title.toLowerCase() === "belemmeringen") {

          // Hide labels for belemmeringen array items
          transformedProperty["items"] = {
            "ui:options": {
              label: false,
            }
          };

          // Set options for the array field itself
          transformedProperty["ui:options"] = {
            ...transformedProperty["ui:options"],
            orderable: false,
            label: false,
          };
        }

        //loop through dependencies
        if (property.dependencies) {
          for (const depKey in property.dependencies) {
            if (property.dependencies.hasOwnProperty(depKey)) {
              const dependency = property.dependencies[depKey];
              if (dependency.oneOf) {
                dependency.oneOf.forEach((subSchema: { properties?: Record<string, JsonSchemaProperty> }) => {
                  if (subSchema.properties) {
                    const nestedTransformed = transformProperties(
                      subSchema.properties,
                      depth + 1,
                      false
                    );
                    const dependentKeyIndex = sortedKeys.indexOf(depKey);
                    if (dependentKeyIndex !== -1) {
                      const dependentKeys = Object.keys(subSchema.properties);
                      sortedKeys.splice(
                        dependentKeyIndex + 1,
                        0,
                        ...dependentKeys
                      );
                      sortedKeys = Array.from(new Set(sortedKeys));
                    }
                    if (Object.keys(nestedTransformed).length > 0) {
                      // Store the current ui:title before merging to prevent overwriting
                      const currentTitle = transformedProperty["ui:title"];
                      
                      // Store existing ui:title values for all nested properties before merging
                      const existingTitles: Record<string, unknown> = {};
                      for (const nestedKey in nestedTransformed) {
                        const nestedProp = (transformedProperty as Record<string, Record<string, unknown>>)[nestedKey];
                        if (nestedProp && nestedProp["ui:title"] !== undefined) {
                          existingTitles[nestedKey] = nestedProp["ui:title"];
                        }
                      }
                      
                      // Perform the merge
                      Object.assign(transformedProperty, nestedTransformed);
                      
                      // Restore any existing ui:title values that got overwritten
                      for (const nestedKey in existingTitles) {
                        const nestedProp = (transformedProperty as Record<string, Record<string, unknown>>)[nestedKey];
                        if (nestedProp) {
                          nestedProp["ui:title"] = existingTitles[nestedKey];
                        }
                      }
                      
                      // Restore the original ui:title if it was set and got overwritten
                      if (currentTitle !== undefined && transformedProperty["ui:title"] !== currentTitle) {
                        transformedProperty["ui:title"] = currentTitle;
                      }
                    }
                  }
                });
              }
            }
          }
        }

        if (sortedKeys.length > 0) {
          transformedProperty["ui:order"] = sortedKeys;
        }

        if (Object.keys(transformedProperty).length > 0) {
          transformed[key] = transformedProperty;
        }
      }
    }

    return transformed;
  }

  const sortedTopLevelKeys = Object.keys(inputJson.properties).sort((a, b) => {
    const orderA = inputJson.properties[a].order ?? 0;
    const orderB = inputJson.properties[b].order ?? 0;
    return orderA - orderB;
  });

  outputJson["ui:order"] = sortedTopLevelKeys;

  const transformedProperties = transformProperties(inputJson.properties);
  const finalOutputJson = { ...outputJson, ...transformedProperties };

  return finalOutputJson;
}

